import React, { useContext, useState, useEffect } from 'react';
import {
  CarouselProvider,
  Slider,
  Slide,
  Dot,
  ButtonNext,
  ButtonBack,
  CarouselContext,
} from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import styled from 'styled-components';
import PropTypes from 'prop-types';

const SLIDE_GUTTER = 25;
const ARROW_SIZE = 44;
const DOT_SIZE = 16;
// PAGINATION ========================

const StyledDots = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  z-index: 2;
  position: relative;
  padding-top: calc(60px - ${(ARROW_SIZE - DOT_SIZE) * 0.5}px);
  margin-bottom: ${(ARROW_SIZE - DOT_SIZE) * -0.5}px;
  @media ${({ theme }) => theme.custom.layouts.below_large} {
    padding-top: calc(30px - ${(ARROW_SIZE - DOT_SIZE) * 0.5}px);
  }
  & > .carousel__dot {
    width: ${ARROW_SIZE}px;
    height: ${ARROW_SIZE}px;
    border: 0;
    outline: none;
    box-shadow: none;
    background-color: transparent;
    position: relative;

    &:after {
      transition: all 0.3s ease;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      position: absolute;
      height: ${DOT_SIZE}px;
      width: ${DOT_SIZE}px;
      content: '';
      display: block;
      border-radius: 50%;
      border: solid 1px
        ${({ theme, variant }) =>
          variant === 'dark' ? 'white' : theme.global.colors.text.dark};
      background-color: transparent;
    }

    @media (hover: hover) {
      &:hover {
        &:after {
          background-color: ${({ theme }) => theme.global.colors.brand};
          border-color: ${({ theme }) => theme.global.colors.brand};
        }
      }
    }

    &--selected {
      &:after {
        background-color: ${({ theme }) => theme.global.colors.brand};
        border-color: ${({ theme }) => theme.global.colors.brand};
      }
    }
  }
`;

const CarouselDots = ({ visibleSlides, totalSlides, variant }) => {
  const dots = [];
  for (let i = 0; i < totalSlides; i += visibleSlides) {
    dots.push(<Dot slide={i} key={i} />);
  }
  return <StyledDots variant={variant}>{dots}</StyledDots>;
};

CarouselDots.propTypes = {
  variant: PropTypes.oneOf(['dark', 'light']).isRequired,
  visibleSlides: PropTypes.number.isRequired,
  totalSlides: PropTypes.number.isRequired,
};

// CONTROLS ========================
const StyledArrowIcon = styled.div`
  width: ${ARROW_SIZE}px;
  height: ${ARROW_SIZE}px;
  display: flex;
  justify-content: center;
  align-items: center;
  transform: ${({ variant }) =>
    variant === 'back' ? 'rotate(180deg)' : 'none'};

  svg path {
    fill: black;
    transform: all 0.3s ease;
  }

  @media (hover: hover) {
    &:hover {
      svg path {
        fill: ${({ theme }) => theme.global.colors.brand};
      }
    }
  }
`;

const ArrowIcon = ({ ...rest }) => (
  <StyledArrowIcon {...rest}>
    <svg
      width="20"
      height="19"
      viewBox="0 0 20 19"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.8542 0L8.48167 2.35667L13.6283 7.50333H0V10.83H13.6283L8.48167 15.9767L10.8542 18.3333L20 9.16667L10.8542 0Z"
        fill="black"
      />
    </svg>
  </StyledArrowIcon>
);

const ButtonsContainerStyle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  width: 100%;
  height: calc(100% - 90px - ${(ARROW_SIZE - DOT_SIZE) * -0.5}px);
  z-index: 1;

  @media ${({ theme }) => theme.custom.layouts.below_large} {
    display: none;
  }
  & > .button-arrow {
    background: none;
    outline: none;
    border: none;
    padding: 0px;
    position: absolute;
    &--back {
      left: calc(-1 * ${ARROW_SIZE}px);
    }
    &--next {
      right: calc(-1 * ${ARROW_SIZE}px);
    }
  }
`;

const ButtonsContainer = ({ totalSlides, step }) => {
  const carouselContext = useContext(CarouselContext);
  const [currentSlide, setCurrentSlide] = useState(
    carouselContext.state.currentSlide,
  );
  useEffect(() => {
    function onChange() {
      setCurrentSlide(carouselContext.state.currentSlide);
    }
    carouselContext.subscribe(onChange);
    return () => carouselContext.unsubscribe(onChange);
  }, [carouselContext]);
  return (
    <ButtonsContainerStyle>
      {currentSlide > 0 && (
        <ButtonBack className="button-arrow button-arrow--back">
          <ArrowIcon variant="back" />
        </ButtonBack>
      )}
      {currentSlide < totalSlides - step && (
        <ButtonNext className="button-arrow button-arrow--next">
          <ArrowIcon variant="next" />
        </ButtonNext>
      )}
    </ButtonsContainerStyle>
  );
};

ButtonsContainer.propTypes = {
  totalSlides: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
};

// CAROUSEL ========================

const StyledCarousel = styled.div`
  position: relative;
  @media ${({ theme }) => theme.custom.layouts.below_large} {
    display: block;
    width: calc(100% + (var(--outerGutter) * 2));
    margin: 0 calc(var(--outerGutter) * -1);
  }

  @media ${({ theme }) => theme.custom.layouts.above_large} {
    width: calc(100% + (${SLIDE_GUTTER}px));
    margin: 0 calc(${SLIDE_GUTTER}px * -0.5);
  }
  .carousel {
    /* height: calc(100% * (var(--gutters) * 2)); */
  }
  .carousel__slider {
    /* THIS IS THE ELEMENT WITH OVERFLOW HIDDEN */
    height: 100%;
    margin: calc(${SLIDE_GUTTER}px * -0.5) 0;
    @media ${({ theme }) => theme.custom.layouts.below_large} {
      margin: calc(${SLIDE_GUTTER}px * -1) 0;
    }
    /* margin: ${SLIDE_GUTTER}px 0; */
  }
  .carousel__slide {
    /* padding: ${SLIDE_GUTTER}px; */
    height: 100%;
  }
  .carousel__inner-slide {
    padding: ${SLIDE_GUTTER}px calc(var(--outerGutter) * 1);
    @media ${({ theme }) => theme.custom.layouts.above_large} {
      padding: calc(${SLIDE_GUTTER}px * 0.5);
    }
  }
  *:focus {
    outline: none !important;
    border: none !important;
  }
`;

const defaultOptions = {
  infinite: true,
  isIntrinsicHeight: true,
  dragEnabled: false,
};

const Carousel = ({ options, children, withArrows, variant }) => {
  const mergedOptions = { ...defaultOptions, ...options };
  mergedOptions.step = mergedOptions.visibleSlides;

  const slides = children?.length ? children : [children];
  const showDots = mergedOptions.totalSlides > mergedOptions.visibleSlides;
  return (
    <StyledCarousel>
      <CarouselProvider {...mergedOptions}>
        <Slider>
          {slides.map((child, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Slide key={index} index={index}>
              {child}
            </Slide>
          ))}
        </Slider>

        {withArrows && (
          <ButtonsContainer
            totalSlides={mergedOptions.totalSlides}
            step={mergedOptions.step}
          />
        )}

        {showDots && (
          <CarouselDots
            variant={variant}
            visibleSlides={mergedOptions.visibleSlides}
            step={mergedOptions.step}
            totalSlides={mergedOptions.totalSlides}
          />
        )}
      </CarouselProvider>
    </StyledCarousel>
  );
};

Carousel.defaultProps = {
  options: defaultOptions,
  withArrows: true,
  variant: 'light',
};

Carousel.propTypes = {
  options: PropTypes.shape({
    infinite: PropTypes.bool,
    visibleSlides: PropTypes.number.isRequired,
    totalSlides: PropTypes.number.isRequired,
  }),
  variant: PropTypes.oneOf(['dark', 'light']),
  withArrows: PropTypes.bool,
  children: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default Carousel;
